import {Injectable} from '@angular/core';
import {AngularFireDatabase} from '@angular/fire/compat/database';
import {AngularFireAuth} from '@angular/fire/compat/auth';
import {trace} from '@angular/fire/compat/performance';
import {TranslateService} from '@ngx-translate/core';
import {Reference} from '@firebase/database-types';
import {from, BehaviorSubject, Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import * as _ from 'lodash';
import firebase from 'firebase/compat/app';
import {getFullName, SnackBarService} from '@q9elements/ui-kit/common';
import {DiagramMetaService} from '../diagram-data/diagram-meta.service';
import {StylesService} from '../diagram-render/styles/styles.service';
import {AuthService} from '../core/auth/auth.service';
import {environment} from '../../environments/environment';
import {GraphModeService} from './graph-mode.service';
import {ModeManagerService} from './mode-manager.service';
import {ModeDataService} from './mode-data.service';
import {ImageUploadService} from '../core/services/image.upload.service';
import {CircularDepCrutch} from '../core/helpers/circular-dep.crutch';
import {DiagramMetaStoreService} from '../diagram-data/diagram-meta-store.service';
import {StickersService} from '../diagram-interaction/services/stickers.service';

/** A marker older than this is left over from a server that stopped mid-write, and is ignored **/
const AGENT_WRITE_TTL_MS = 2 * 60 * 1000;

@Injectable()
export class FirebaseDataService extends CircularDepCrutch {
  meta: any = {};

  private readonly SESSION_TTL_MS = 24 * 60 * 60 * 1000; // 1 day;
  public mapCacheRef;
  public diagramContentRef: Reference;
  public diagramAIGenerationRef: Reference;
  public dvGenerationRef: Reference;
  private agentWriteRef: Reference;
  public diagramUPNRecommendationsGenerationRef: Reference;
  public diagramAgentInstructionsRecommendationsGenerationRef: Reference;
  public diagramAgentFinderRecommendationsGenerationRef: Reference;
  public diagramActivitiesRef: Reference;
  private imagesRef: Reference;
  private stickersRef: Reference;
  public diagramLockRef: Reference;
  private lastActionRef: Reference;
  public usersRef: Reference;
  private userRef: Reference;
  public stylesRef;
  private isOnlineRef: Reference;
  private offsetRef: Reference;
  public isOnline: boolean;
  private unlockWithRemove: boolean;
  private lastActivity: number; // timestamp
  /** True iff this session acquired the lock via lock() and has not been kicked/released since **/
  private holdsLock = false;
  /** Deferred takeover kick, applied by the poller; latched — a lock re-read cannot detect it **/
  private pendingKick = false;

  /** Injected in corresponding services constructors to avoid circular dependency **/
  public imageSvc: ImageUploadService;
  public stickersSvc: StickersService;
  private _refsInitialized$ = new BehaviorSubject(false);
  public refsInitialized$ = this._refsInitialized$.asObservable();

  constructor(
    private afdb: AngularFireDatabase,
    private afAuth: AngularFireAuth,
    private metaSvc: DiagramMetaService,
    private styleSvc: StylesService,
    private authSvc: AuthService,
    private modeManSvc: ModeManagerService,
    private modeDataSvc: ModeDataService,
    private graphModeSvc: GraphModeService,
    private snackBarSvc: SnackBarService,
    private diagramMetaStoreService: DiagramMetaStoreService,
    private translateSvc: TranslateService
  ) {
    super();
    this.unlockWithRemove = true;
    this.trackLastActivity();
  }

  setup() {
    this.meta = this.metaSvc.getMetaSync();

    return this.authenticate(this.meta['authToken'])
      .pipe(
        tap(() => {
          this.cleanUpReferences();
          this.setupReferences();
          this.setUserCache();
          this.setupEvents();
          this.handleConnection();
        })
      )
      .toPromise()
      .then(() => {
        this.stylesRef.once('value').then(snapshot => {
          this.styleSvc.setPrimary(snapshot.val());
        });

        return from(this.diagramContentRef.once('value'))
          .pipe(environment.fbConfig.trace ? trace('diagram-load') : tap())
          .toPromise()
          .then(snapshot => (snapshot.exists() ? snapshot.val() : null));
      });
  }

  authenticate(token) {
    return from(this.afAuth.signInWithCustomToken(token));
  }

  cleanUpReferences() {
    this.mapCacheRef && this.mapCacheRef.off();
    this.diagramContentRef && this.diagramContentRef.off();
    this.imagesRef && this.imagesRef.off();
    this.stickersRef && this.stickersRef.off();
    this.diagramLockRef && this.diagramLockRef.off();
    this.lastActionRef && this.lastActionRef.off();
    this.usersRef && this.usersRef.off();

    if (this.usersRef) {
      this.userRef.off();
      this.userRef.remove();
    }

    this.stylesRef && this.stylesRef.off();
    this.isOnlineRef && this.isOnlineRef.off();
    this.offsetRef && this.offsetRef.off();
    this.diagramActivitiesRef && this.diagramActivitiesRef.off();
  }

  setupReferences(): void {
    this.mapCacheRef = this.afdb.database.ref(this.meta['mapCachePath']);
    this.diagramContentRef = this.afdb.database.ref(this.meta['diagramContentPath']);
    this.diagramAIGenerationRef = this.afdb.database.ref(this.meta['diagramAIGenerationPath']);
    this.diagramActivitiesRef = this.afdb.database.ref(
      this.meta['diagramContentPath'] + '/activities'
    );
    this.imagesRef = this.afdb.database.ref(this.meta['diagramContentPath'] + '/images');
    this.stickersRef = this.afdb.database.ref(this.meta['diagramContentPath'] + '/notes');
    this.diagramLockRef = this.afdb.database.ref(this.meta['diagramLockPath']);
    this.lastActionRef = this.afdb.database.ref(this.meta['diagramLastActionPath']);
    this.usersRef = this.afdb.database.ref(this.meta['diagramUsersPath']);
    this.userRef = this.usersRef.push();
    this.stylesRef = this.afdb.database.ref(this.meta['stylePath']);
    this.isOnlineRef = this.afdb.database.ref('.info/connected');
    this.offsetRef = this.afdb.database.ref('.info/serverTimeOffset');

    if (this.meta['diagramAIGenerationPathDV']) {
      this.dvGenerationRef = this.afdb.database.ref(this.meta['diagramAIGenerationPathDV']);
    }

    // cleared when absent: setup() runs again on navigation, and a previous diagram's marker must not be read
    this.agentWriteRef = this.meta['diagramAgentWritePath']
      ? this.afdb.database.ref(this.meta['diagramAgentWritePath'])
      : undefined;

    if (this.meta['diagramUPNRecommendationsGenerationPath']) {
      this.diagramUPNRecommendationsGenerationRef = this.afdb.database.ref(
        this.meta['diagramUPNRecommendationsGenerationPath']
      );
    }

    if (this.meta['diagramAgentInstructionsRecommendationsGenerationPath']) {
      this.diagramAgentInstructionsRecommendationsGenerationRef = this.afdb.database.ref(
        this.meta['diagramAgentInstructionsRecommendationsGenerationPath']
      );
    }

    if (this.meta['diagramAgentFinderRecommendationsGenerationPath']) {
      this.diagramAgentFinderRecommendationsGenerationRef = this.afdb.database.ref(
        this.meta['diagramAgentFinderRecommendationsGenerationPath']
      );
    }

    this.cleanUpUsers();
    this._refsInitialized$.next(true);
  }

  private cleanUpUsers(): void {
    this.usersRef.once('value', usersSnapshot => {
      usersSnapshot.forEach(userSnapshot => {
        if (Date.now() - userSnapshot.val().lastSeen > this.SESSION_TTL_MS) {
          userSnapshot.ref.remove();
        }
      });
    });
  }

  setupEvents() {
    this.setStyleEvent();
    this.setLockEvent();
    this.setStickerEvent();
    this.setImageEvent();
  }

  public setStyleEvent(path?): Promise<void> {
    if (path) {
      this.stylesRef.off();
      this.stylesRef = this.afdb.database.ref(path);
    }

    let themeSwitch = true;

    return this.stylesRef.on('value', snapshot => {
      const theme = snapshot.val();
      const isViewMode = this.modeDataSvc.isViewMode();

      if (theme && (themeSwitch || isViewMode)) {
        this.styleSvc.setPrimary(theme);
      }

      if (!isViewMode && theme && !themeSwitch) {
        this.snackBarSvc.info(this.translateSvc.instant('POPUPS.THEME_CHANGED'));
      }

      themeSwitch = false;
    });
  }

  public setCleanClientCacheEvent(): void {
    this.mapCacheRef.on('value', snapshot => {
      const timestamp = snapshot.val();

      if (!timestamp) {
        return;
      }

      this.diagramMetaStoreService.clearClientCache(timestamp);
    });
  }

  private setStickerEvent() {
    this.stickersRef.off();
    this.stickersRef.on('child_changed', snapshot => {
      const sticker = snapshot.val();

      if (this.stickersSvc) {
        this.stickersSvc.updateExisting(sticker);
      }
    });
    this.stickersRef.on('child_added', snapshot => {
      const sticker = snapshot.val();

      if (this.stickersSvc) {
        this.stickersSvc.createExisting(sticker);
      }
    });
    this.stickersRef.on('child_removed', () => {
      this.stickersRef.once('value', snapshot => {
        // 'child_removed' event will send wrong data in our case, it is quite hard to fix, so instead,
        // if such event occurs we take the list of all notes and check if it corresponds to list of rendered notes
        const stickers = snapshot.val() ?? [];

        if (this.stickersSvc) {
          this.stickersSvc.checkAndRemoveExisting(stickers);
        }
      });
    });
  }

  private setImageEvent() {
    this.imagesRef.off();
    this.imagesRef.on('child_changed', snapshot => {
      const image = snapshot.val();
      const updateInfo = {
        mxKey: image.mxKey,
        url: image.imageResized,
        uuid: image.uuid
      };

      if (this.imageSvc) {
        this.imageSvc.updateExisting(updateInfo);
      }
    });
    this.imagesRef.on('child_added', snapshot => {
      const image = snapshot.val();
      const updateInfo = {
        mxKey: image.mxKey,
        url: image.imageResized,
        uuid: image.uuid
      };

      if (this.imageSvc) {
        this.imageSvc.updateExisting(updateInfo);
      }
    });
  }

  lock(): Promise<any> {
    if (!this.isOnline) {
      return Promise.reject({error: 'failed'});
    }

    const currUser = this.authSvc.getUserDataSync();
    let serverOffset = 0;

    return Promise.all([
      this.lastActionRef.once('value'),
      this.offsetRef.once('value'),
      this.dvGenerationRef ? this.dvGenerationRef.once('value') : Promise.resolve(null),
      this.agentWriteRef ? this.agentWriteRef.once('value') : Promise.resolve(null)
    ])
      .then(snapshots => {
        const lastAction = snapshots[0].val();

        serverOffset = snapshots[1].val();

        const inactivity = serverOffset + Date.now() - lastAction;
        const dvGeneration = snapshots[2] && snapshots[2].val();
        const agentWrite = snapshots[3] && snapshots[3].val();

        if (dvGeneration) {
          throw {error: 'locked', byElements: true};
        }

        if (this.isFreshAgentWrite(agentWrite, serverOffset)) {
          throw {error: 'locked', byAgent: true, user: agentWrite.name};
        }

        return this.diagramLockRef.transaction(currentLock => {
          if (!currentLock) {
            return currUser.id;
          }

          if (this.isOwnLockId(currentLock, currUser.id)) {
            // release first: the null is the takeover signal that kicks the user's other session
            return null;
          }

          if (inactivity > environment.REMOTE_UNLOCK_TTL) {
            return currUser.id;
          }

          return undefined; // abort: actively locked by another user
        });
      })
      .then((result: any) => {
        if (result.committed && result.snapshot.val() === null) {
          // phase 2 of the takeover: claim the lock we just released
          return this.diagramLockRef.transaction(currentLock =>
            currentLock === null ? currUser.id : undefined
          );
        }

        return result;
      })
      .then((result: any) => {
        if (!result.committed || !this.agentWriteRef) {
          return result;
        }

        // an agent save that started after the first read checks the lock after setting its marker; check it again.
        // lastAction goes first: the server ignores a lock whose lastAction is old, so it must see this one as fresh
        return this.lastActionRef
          .set(firebase.database.ServerValue.TIMESTAMP)
          .then(() => this.agentWriteRef.once('value'))
          .then(snapshot => {
            const agentWrite = snapshot.val();

            if (!this.isFreshAgentWrite(agentWrite, serverOffset)) {
              return result;
            }

            return this.diagramLockRef
              .transaction(currentLock =>
                this.isOwnLockId(currentLock, currUser.id) ? null : undefined
              )
              .then(() => {
                throw {error: 'locked', byAgent: true, user: agentWrite.name};
              });
          });
      })
      .then((result: any) => {
        if (result.committed) {
          this.diagramLockRef.onDisconnect().remove();
          this.lastActivity = Date.now();
          this.setLastAction(true);
          this.unlockWithRemove = true;
          this.holdsLock = true;
          // undo the self-inflicted latch from our own phase-1 null
          this.pendingKick = false;

          return currUser.id;
        }

        const lockId = result.snapshot && result.snapshot.val();

        return this.usersRef.once('value').then(usersSnapshot => {
          const usersObj = usersSnapshot.val();
          const lockUserKey = _.findKey(usersObj, user => String(user.id) === String(lockId));
          const lockedBy = getFullName(usersObj[lockUserKey]);

          throw {
            error: 'locked',
            user: lockedBy
          };
        });
      })
      .catch(e => {
        if (e.error === 'locked') {
          throw e;
        } else {
          throw {error: 'failed'};
        }
      });
  }

  public unlock() {
    if (this.diagramLockRef) {
      if (this.isOnline && this.unlockWithRemove) {
        // never delete another user's fresh lock
        this.diagramLockRef.transaction(currentLock =>
          !currentLock || this.isOwnLockId(currentLock) ? null : undefined
        );
      }

      this.diagramLockRef.onDisconnect().cancel();
      this.unlockWithRemove = true;
      this.holdsLock = false;
    }
  }

  public unlockSelf(): Promise<void> {
    if (!this.unlockWithRemove) {
      // lost the lock (kick/takeover): not ours to release; only the next lock() resets the flag
      return Promise.resolve();
    }

    return this.diagramLockRef
      .once('value')
      .then(user => user.val())
      .then(lockUserId => {
        if (this.isOwnLockId(lockUserId)) {
          this.setWithRemove(true);

          return this.unlock();
        }
      });
  }

  public setLockEvent() {
    this.diagramLockRef.off();
    this.diagramLockRef.on('value', snapshot => {
      if (this.isOwnLockId(snapshot.val())) {
        return;
      }

      // lost the lock: disarm onDisconnect so it cannot delete a later session's lock;
      // latch the kick — goViewMode() no-ops during an active navigation, the poller retries
      if (this.graphModeSvc.get()) {
        this.disarmLock(true);
        this.modeManSvc.goViewMode();
      } else if (this.holdsLock) {
        this.disarmLock(true);
      }
    });
  }

  private isFreshAgentWrite(agentWrite, serverOffset: number): boolean {
    return Boolean(agentWrite) && serverOffset + Date.now() - agentWrite.at < AGENT_WRITE_TTL_MS;
  }

  private isOwnLockId(lockId: any, userId = this.authSvc.getUserDataSync().id): boolean {
    return Boolean(lockId) && String(lockId) === String(userId);
  }

  private disarmLock(pendingKick: boolean): void {
    this.diagramLockRef.onDisconnect().cancel();
    this.unlockWithRemove = false;
    this.holdsLock = false;
    this.pendingKick = pendingKick;
  }

  public setWithRemove(bool: boolean): void {
    this.unlockWithRemove = bool;
  }

  private setUserCache() {
    if (this.userRef && this.isOnline) {
      const user = _.pick(this.authSvc.getUserDataSync(), [
        'id',
        'firstName',
        'lastName',
        'avatar_sm',
        'avatar_md',
        'avatar_lg'
      ]);

      user['lastSeen'] = firebase.database.ServerValue.TIMESTAMP;
      this.userRef.set(user);
    }
  }

  handleConnection() {
    let firstLoad = true;

    this.isOnlineRef.on('value', snapshot => {
      this.isOnline = Boolean(snapshot.val());
      this.userRef.onDisconnect().remove();
      this.setUserCache();

      if (!firstLoad) {
        this.modeManSvc.goViewMode();
      }

      firstLoad = !this.isOnline ? firstLoad : false;
    });
  }

  public setLastAction(force?: boolean): void {
    if ((this.graphModeSvc.get() || force) && this.isOnline) {
      // firebase.default.database.ServerValue.TIMESTAMP
      this.lastActionRef.set(firebase.database.ServerValue.TIMESTAMP);
    }
  }

  private trackLastActivity() {
    document.addEventListener('click', () => {
      const currentLocalTime = Date.now();

      if (this.lastActivity && currentLocalTime - this.lastActivity > 500) {
        this.setLastAction();
      }

      this.lastActivity = currentLocalTime;
    });
    setInterval(() => {
      if (!this.graphModeSvc.get()) {
        return;
      }

      if (this.pendingKick) {
        // not cleared here: a kick no-op'd by an in-flight navigation retries next tick
        this.modeManSvc.goViewMode();

        return;
      }

      if (this.lastActivity && Date.now() - this.lastActivity >= environment.REMOTE_UNLOCK_TTL) {
        this.modeManSvc.goViewMode();
      }
    }, 3000);
  }

  subscribeForTask(ref): Observable<any> {
    return this.afdb.object(ref).snapshotChanges();
  }
}
